import { useState, useEffect, useCallback } from 'react'
import { ChevronRight, Rocket, Monitor, AppWindow, BarChart3 } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { GuideSectionMeta } from '../../lib/api'

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  Monitor,
  AppWindow,
  BarChart3,
}

interface GuideTreeProps {
  sections: GuideSectionMeta[]
  activeSlug: string
  onSelect: (slug: string) => void
  className?: string
}

/**
 * Find all ancestor slugs for a given slug in the tree.
 * Returns the slugs of parent nodes that should be expanded.
 */
function findAncestors(sections: GuideSectionMeta[], targetSlug: string): string[] {
  const ancestors: string[] = []

  function walk(nodes: GuideSectionMeta[], path: string[]): boolean {
    for (const node of nodes) {
      if (node.slug === targetSlug) {
        ancestors.push(...path)
        return true
      }
      if (node.children.length > 0) {
        if (walk(node.children, [...path, node.slug])) {
          return true
        }
      }
    }
    return false
  }

  walk(sections, [])
  return ancestors
}

function TreeItem({
  node,
  activeSlug,
  expanded,
  onToggle,
  onSelect,
  depth = 0,
}: {
  node: GuideSectionMeta
  activeSlug: string
  expanded: Set<string>
  onToggle: (slug: string) => void
  onSelect: (slug: string) => void
  depth?: number
}) {
  const isActive = activeSlug === node.slug
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.slug)
  const Icon = node.icon ? iconMap[node.icon] : undefined

  return (
    <li>
      <div
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
          depth > 0 && 'pl-4'
        )}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.slug) }}
            className="p-0.5 rounded hover:bg-secondary/50 transition-colors flex-shrink-0"
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.title}`}
          >
            <ChevronRight
              className={cn(
                'w-3.5 h-3.5 transition-transform',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}
        <button
          onClick={() => {
            onSelect(node.slug)
            if (hasChildren && !isExpanded) {
              onToggle(node.slug)
            }
          }}
          className="flex items-center gap-2 truncate min-w-0 text-left flex-1"
        >
          {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
          <span className="truncate">{node.title}</span>
        </button>
      </div>
      {hasChildren && isExpanded && (
        <ul className="ml-2">
          {node.children
            .sort((a, b) => a.order - b.order)
            .map((child) => (
              <TreeItem
                key={child.slug}
                node={child}
                activeSlug={activeSlug}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ))}
        </ul>
      )}
    </li>
  )
}

export function GuideTree({ sections, activeSlug, onSelect, className }: GuideTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Auto-expand ancestors of active slug on mount and when activeSlug changes
  useEffect(() => {
    if (!activeSlug || sections.length === 0) return
    const ancestors = findAncestors(sections, activeSlug)
    if (ancestors.length > 0) {
      setExpanded((prev) => {
        const next = new Set(prev)
        for (const a of ancestors) {
          next.add(a)
        }
        return next
      })
    }
  }, [activeSlug, sections])

  const handleToggle = useCallback((slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) {
        next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }, [])

  return (
    <nav className={className}>
      <ul className="space-y-0.5">
        {sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <TreeItem
              key={section.slug}
              node={section}
              activeSlug={activeSlug}
              expanded={expanded}
              onToggle={handleToggle}
              onSelect={onSelect}
            />
          ))}
      </ul>
    </nav>
  )
}
